import { FunctionComponent, memo, useEffect } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { IExamdate } from "./IExamDate";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addExamdate } from "../../Redux/Slices/Examdate/AddExamdateSlice";
import { EditExamdate } from "../../Redux/Slices/Examdate/EditExamdateSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import DatePickers from "../../Components/DatePicker/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface ExamdateFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IExamdate;
  setInitialValues: React.Dispatch<React.SetStateAction<IExamdate>>;
}

const ExamdateForm: FunctionComponent<ExamdateFormProps> = (props) => {
  const dispatch = useAppDispatch();

  const userSchema = z.object({
    id: z.number().optional(),
    name: z
      .string({ message: "Please enter name" })
      .min(1, { message: "Please enter name" }),
    yeargroup_country_map_id: z.number({
      required_error: "Please select year group and country",
      invalid_type_error: "Please select year group and country",
    }),

    exam_date: z
      .preprocess(
        (arg) =>
          typeof arg === "string" || arg instanceof Date ? dayjs(arg) : arg,
        z.custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
          message: "Please select a valid exam date",
        })
      )
      .refine((val) => val && val.isAfter(dayjs()), {
        message: "Date must be in the future",
      }),
    status: z.boolean({
      message: "Please select the status",
    }),
  });
  const methods = useForm<IExamdate>({
    defaultValues: {
      ...props.initialValues,
      exam_date: props.initialValues.exam_date
        ? dayjs(props.initialValues.exam_date)
        : null,
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const { data: listYearlevel_maps } = useAppSelector(
    (state) => state.ListYearlevel_mapSlice
  );

  const ListCountry = Array.isArray(listYearlevel_maps?.data)
    ? listYearlevel_maps?.data.map((listQuestions) => ({
        label:
          `${listQuestions?.yeargroup?.name}-${listQuestions?.region?.name}-${listQuestions?.region?.state}`!,
        value: listQuestions.id,
      }))
    : [];

  const onSubmit: SubmitHandler<IExamdate> = (val) => {
    const formattedData: IExamdate = {
      ...val,
      exam_date: val.exam_date
        ? dayjs(val.exam_date).format("YYYY-MM-DD")
        : null,
    };

    if (formattedData.id) {
      dispatch(EditExamdate(formattedData));
    } else {
      dispatch(addExamdate(formattedData));
    }
  };

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const ExamdateSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_content"
      >
        <TextInputFieldC name="name" label="Name" type="text" />

        <Select
          name="yeargroup_country_map_id"
          label="Select  the country"
          options={ListCountry ?? []}
          value={
            ListCountry?.find(
              (list) =>
                list.value === methods.getValues("yeargroup_country_map_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("yeargroup_country_map_id", val?.value, {
              shouldValidate: true,
            });
          }}
        />
        {/* <DatePickers
          name="startDate"
          // id="startDate"
          label="Start Date"
          // maxDate={methods.getValues().endDate}
          onChange={() => methods.trigger("exam_date")}
        /> */}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <Controller
              name="exam_date"
              control={methods.control}
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Exam Date"
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  slotProps={{
                    textField: {
                      helperText: fieldState.error?.message,
                      error: !!fieldState.error,
                      fullWidth: true,
                    },
                  }}
                />
              )}
            />
          </DemoContainer>
        </LocalizationProvider>
        <Select
          name="status"
          label="Status"
          options={ExamdateSatus}
          value={
            ExamdateSatus?.find(
              (role) => role.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(ExamdateForm);
